import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../shared/VideoDialog.module.scss'; // Используем те же стили
import UploadIcon from '../icons/UploadIcon';
import type { IStreamRecording } from '../../types';

interface PublishRecordingDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: PublishRecordingFormData) => Promise<void>;
    recording: IStreamRecording | null;
}

export interface PublishRecordingFormData {
    title: string;
    description: string;
    publishTime: string;
    publishDate: string;
    thumbnailFile?: File;
}

const PublishRecordingDialog: React.FC<PublishRecordingDialogProps> = ({
    open,
    onClose,
    onSubmit,
    recording
}) => {
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [publishTime, setPublishTime] = useState('10:00');
    const [publishDate, setPublishDate] = useState(new Date().toISOString().split('T')[0]);
    const [thumbnailFile, setThumbnailFile] = useState<File | undefined>(undefined);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const thumbnailInputRef = useRef<HTMLInputElement>(null);

    // Сброс формы
    const handleReset = useCallback(() => {
        setTitle('');
        setDescription('');
        setPublishTime('10:00');
        setPublishDate(new Date().toISOString().split('T')[0]);
        setThumbnailFile(undefined);
        setError('');
        onClose();
    }, [onClose]);

    // Обработка клика по фону
    const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !isSubmitting) {
            handleReset();
        }
    }, [isSubmitting, handleReset]);

    // Управление прокруткой страницы
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [open]);

    // Инициализация формы на основе записи
    useEffect(() => {
        if (open && recording) {
            setTitle(recording.title || `Запись #${recording.id}`);
            setDescription('');
            setPublishTime('10:00');
            setPublishDate(new Date().toISOString().split('T')[0]);
            setThumbnailFile(undefined);
            setError('');
        }
    }, [open, recording]);

    // Обработчики для превью
    const handleThumbnailButtonClick = () => {
        if (thumbnailInputRef.current) {
            thumbnailInputRef.current.click();
        }
    };

    const handleThumbnailFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || undefined;
        setThumbnailFile(file);
    };

    // Валидация и отправка формы
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Валидация
        if (!title.trim()) {
            setError('Заголовок обязателен');
            return;
        }

        if (!description.trim()) {
            setError('Описание обязательно');
            return;
        }

        try {
            setIsSubmitting(true);

            await onSubmit({
                title: title.trim(),
                description: description.trim(),
                publishTime,
                publishDate,
                thumbnailFile
            });

            handleReset();
        } catch (err) {
            setError('Ошибка при публикации записи');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Обработчик закрытия для кнопки закрытия
    const handleCloseClick = () => {
        if (!isSubmitting) {
            handleReset();
        }
    };

    if (!open) return null;

    return (
        <div className={styles.dialogOverlay} onClick={handleBackdropClick}>
            <div className={styles.dialogContainer}>
                <div className={styles.dialogHeader}>
                    <h2 className={styles.dialogTitle}>Публиковать запись</h2>
                    <button
                        className={styles.closeButton}
                        onClick={handleCloseClick}
                        aria-label={t('common.close')}
                        disabled={isSubmitting}
                    >
                        ✕
                    </button>
                </div>
                <div className={styles.dialogContent}>
                    <form onSubmit={handleSubmit} className={styles.formContainer}>
                        <div className={styles.formField}>
                            <label htmlFor="title" className={styles.label}>
                                Заголовок *
                            </label>
                            <input
                                id="title"
                                className={styles.input}
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                required
                                disabled={isSubmitting}
                                placeholder="Введите заголовок тренировки"
                            />
                        </div>

                        <div className={styles.formField}>
                            <label htmlFor="description" className={styles.label}>
                                Описание *
                            </label>
                            <textarea
                                id="description"
                                className={styles.textarea}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={4}
                                required
                                disabled={isSubmitting}
                                placeholder="Введите описание тренировки"
                            />
                        </div>

                        <div className={styles.formField}>
                            <label htmlFor="publishDate" className={styles.label}>
                                Дата публикации
                            </label>
                            <input
                                id="publishDate"
                                className={styles.input}
                                type="date"
                                value={publishDate}
                                onChange={(e) => setPublishDate(e.target.value)}
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className={styles.formField}>
                            <label htmlFor="publishTime" className={styles.label}>
                                Время публикации
                            </label>
                            <input
                                id="publishTime"
                                className={styles.input}
                                type="time"
                                value={publishTime}
                                onChange={(e) => setPublishTime(e.target.value)}
                                disabled={isSubmitting}
                            />
                        </div>

                        <div>
                            <p className={styles.subtitle}>Превью</p>
                            <input
                                className={styles.fileInput}
                                accept="image/*"
                                id="thumbnail-file"
                                type="file"
                                ref={thumbnailInputRef}
                                onChange={handleThumbnailFileChange}
                                disabled={isSubmitting}
                            />
                            <label htmlFor="thumbnail-file" className={styles.fileInputLabel}>
                                <button
                                    type="button"
                                    className={`${styles.button} ${styles.buttonOutlined}`}
                                    onClick={handleThumbnailButtonClick}
                                    disabled={isSubmitting}
                                >
                                    <UploadIcon
                                        width={18}
                                        height={18}
                                        style={{ marginRight: '8px' }}
                                    />
                                    {thumbnailFile ? thumbnailFile.name : 'Загрузить превью'}
                                </button>
                            </label>
                        </div>

                        {error && <p className={styles.errorText}>{error}</p>}
                    </form>
                </div>
                <div className={styles.dialogActions}>
                    <button
                        onClick={handleCloseClick}
                        className={styles.button}
                        disabled={isSubmitting}
                    >
                        Отмена
                    </button>
                    <button
                        onClick={handleSubmit}
                        className={`${styles.button} ${styles.buttonContained}`}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <span className={styles.spinnerContainer}>
                                <span className={styles.spinner}></span>
                                <span style={{ marginLeft: '8px' }}>Публикуется...</span>
                            </span>
                        ) : 'Опубликовать'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PublishRecordingDialog;
